import {
  MotherDto,
  SiteAddressDto,
  UserInfoDto,
  VisitDto,
} from '@ecdlink/core';
import {
  EventRecordType,
  MotherModelInput,
  SiteAddressInput,
  VisitDataStatus,
  VisitDataStatusFilterInput,
  VisitModelInput,
  MotherChildVisitModelInput,
} from '@ecdlink/graphql';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { MotherService } from '@services/MotherService';
import { RootState, ThunkApiType } from '../types';
import { Referral } from '@/services/ReferralService';
import { UpdateMotherDeliveryDateProps } from './mother.types';
import { EventRecordService } from '@/services/EventRecordService';

export const MotherActions = {
  GET_MOTHERS: 'getMothers',
  GET_MOTHER_VISITS: 'getMotherVisits',
  GET_MOTHER_EVENT_RECORD_TYPES: 'getMotherEventRecordTypes',
  ADD_MOTHER: 'addMother',
  GET_MOTHER_COUNT_FOR_MONTH: 'getMotherCountForMonth',
  UPDATE_MOTHER_ADDRESS: 'updateMotherAddress',
  ADD_ADDITIONAL_VISIT_FOR_MOTHER: 'addAdditionalVisitForMother',
  GET_REFERRALS_FOR_MOTHER: 'getReferralsForMother',
  GET_COMPLETED_REFERRALS_FOR_MOTHER: 'getCompletedReferralsForMother',
  UPDATE_VISIT_DATA_STATUS: 'updateVisitDataStatus',
  UPDATE_MOTHER_DELIVERY_DATE: 'updateMotherDeliveryDate',
  GET_MOTHERS_STATUS_INFO_FOR_IDS: 'getMotherStatusInfoForIds',
  GET_ALL_VISITS_FOR_MOTHER_IDS: 'getAllVisitsForMotherIds',
  SYNC_MOTHER_VISITS: 'syncMotherVisits',
  SYNC_MOTHER_EVENT_RECORDS: 'syncMotherEventRecords',
};

export const syncMotherVisits = createAsyncThunk<
  any,
  void, // argument type (none)
  ThunkApiType<RootState>
>(
  MotherActions.SYNC_MOTHER_VISITS,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      mothers: { mothers },
    } = getState();

    if (!userAuth?.auth_token || !mothers?.length) {
      return { success: true }; // Nothing to sync
    }

    try {
      if (userAuth?.auth_token && mothers) {
        for (const mother of mothers) {
          const unsyncedVisits =
            mother.visits?.filter((v) => 'synced' in v && !v.synced) ?? [];
          if (unsyncedVisits.length > 0) {
            await new MotherService(userAuth?.auth_token).syncMotherVisits(
              unsyncedVisits
            );
          }
        }
      }
      return [true];
    } catch (err) {
      console.error('Failed to sync mother visits:', err);
      return rejectWithValue(err);
    }
  }
);

export const syncMotherEventRecords = createAsyncThunk<
  any,
  void, // argument type (none)
  ThunkApiType<RootState>
>(
  MotherActions.SYNC_MOTHER_EVENT_RECORDS,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      mothers: { mothers },
    } = getState();

    if (!userAuth?.auth_token || !mothers?.length) {
      return { success: true }; // Nothing to sync
    }

    try {
      if (userAuth?.auth_token && mothers) {
        for (const mother of mothers) {
          if (mother.eventRecord && !mother.eventRecord.synced) {
            await new EventRecordService(userAuth?.auth_token).addEventRecord(
              mother.eventRecord
            );
          }
        }
      }
      return [true];
    } catch (err) {
      console.error('Failed to sync mother event record:', err);
      return rejectWithValue(err);
    }
  }
);

export const getMothers = createAsyncThunk<
  MotherDto[], // fulfilled return type
  void, // argument type (none)
  { state: RootState; rejectValue: string | unknown } // ThunkAPI config
>(MotherActions.GET_MOTHERS, async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    mothers: { mothers: cache },
    user: { user: userCache },
  } = getState();

  const cacheBelongsToAuthUser =
    !!userAuth?.id && (!userCache?.id || userCache.id === userAuth.id);

  // Return cached data immediately if present and for this user
  if (cacheBelongsToAuthUser && cache && cache.length > 0) {
    return cache;
  }

  // Ensure we have a token
  if (!userAuth?.auth_token || !userAuth?.id) {
    return rejectWithValue(
      'no access token or user id, authentication required'
    );
  }

  try {
    return await new MotherService(userAuth.auth_token).getMothers();
  } catch (err: any) {
    // Preserve the actual error for debugging/logging
    console.error('Failed to fetch mothers:', err);
    // You can customize the rejected payload here
    return rejectWithValue(
      err?.response?.data?.message || err?.message || 'Failed to fetch mothers'
    );
  }
});

export const getMotherStatusInfoForIds = createAsyncThunk<
  UserInfoDto[], // Returned on success
  void, // Argument type
  { state: RootState; rejectValue: string } // Updated rejectValue to string
>(
  MotherActions.GET_MOTHERS_STATUS_INFO_FOR_IDS,
  async (_, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      mothers: { mothers },
    } = getState();

    if (!mothers) return []; // This returns an empty array on success

    const motherIds = mothers
      .map((m) => m.isActive && m?.user?.id!)
      .filter(Boolean) as string[]; // Filter and cast to string[]

    if (!userAuth?.auth_token || !userAuth?.id) {
      return rejectWithValue(
        'No access token or user ID; authentication required'
      );
    }

    try {
      const mothersInfo = await new MotherService(
        userAuth.auth_token
      ).getMotherStatusInfoForIds(motherIds);
      return mothersInfo; // This will be the success payload
    } catch (err: any) {
      console.error('Failed to fetch mothers info:', err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch mothers info'
      ); // Now this matches the rejectValue type (string)
    }
  }
);

export const getAllVisitsForMotherIds = createAsyncThunk<
  VisitDto[],
  string[],
  { state: RootState; rejectValue: string | unknown }
>(
  MotherActions.GET_ALL_VISITS_FOR_MOTHER_IDS,
  async (motherIds, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    if (!userAuth?.auth_token) {
      return rejectWithValue('no access token, profile check required');
    }

    try {
      const visits = await new MotherService(
        userAuth.auth_token
      ).getAllVisitsForMotherIds(motherIds);

      return visits;
    } catch (err: any) {
      return rejectWithValue(err.message || err);
    }
  }
);

export const upsertMothers = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>('upsertMothers', async (_, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
    mothers: { mothers },
  } = getState();

  try {
    if (userAuth?.auth_token && mothers) {
      for (const mother of mothers) {
        const input = mapMother(mother);

        if (mother.siteAddress) {
          const addressInput = mapSiteAddress(mother.siteAddress);
          input.siteAddressId = addressInput.Id;
        }

        if ('isNew' in mother && mother.isNew === true) {
          const mappedVisits = mother.visits!.map((item) => mapVisit(item));
          await new MotherService(userAuth?.auth_token).addMother(
            input,
            mappedVisits
          );
        } else {
          if ('synced' in mother && mother.synced === false) {
            await new MotherService(userAuth?.auth_token).updateMother(
              mother.id ?? '',
              input
            );
          }
        }
      }
    }
    return [true];
  } catch (err) {
    return rejectWithValue(err);
  }
});

type CreateMotherRequest = {
  mother: MotherDto;
};

export const addMother = createAsyncThunk<
  MotherDto,
  CreateMotherRequest,
  ThunkApiType<RootState>
>(
  MotherActions.ADD_MOTHER,
  async ({ mother }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedMotherInput = mapMother(mother);

      if (userAuth?.auth_token) {
        const mappedVisits = mother.visits!.map((item) => mapVisit(item));
        return await new MotherService(userAuth?.auth_token).addMother(
          mappedMotherInput,
          mappedVisits
        );
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export type UpdateMotherRequest = {
  id: string;
  mother: MotherDto;
};

export const updateMother = createAsyncThunk<
  MotherDto,
  UpdateMotherRequest,
  ThunkApiType<RootState>
>('updateMother', async ({ mother, id }, { getState, rejectWithValue }) => {
  const {
    auth: { userAuth },
  } = getState();
  try {
    if (userAuth?.auth_token) {
      return await new MotherService(userAuth?.auth_token).updateMother(
        id,
        mother as MotherModelInput
      );
    } else {
      return rejectWithValue('no access token, profile check required');
    }
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const getMotherVisits = createAsyncThunk<
  VisitDto[],
  { motherId: string },
  ThunkApiType<RootState>
>(
  MotherActions.GET_MOTHER_VISITS,
  async ({ motherId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      mothers: { mothers },
    } = getState();

    // Find the mother in state
    const mother = mothers?.find(
      (m) => m.id === motherId || m.userId === motherId
    );
    const cachedVisitsForMother = mother?.visits || [];

    // Return cached data if exists and has at least one visit
    if (cachedVisitsForMother.length > 0) {
      return cachedVisitsForMother;
    }

    // No cache — fetch from server
    if (!userAuth?.auth_token) {
      return rejectWithValue('no access token, profile check required');
    }

    try {
      const visits = await new MotherService(
        userAuth.auth_token
      ).getMotherVisits(motherId);

      return visits;
    } catch (err: any) {
      return rejectWithValue(err.message || err);
    }
  }
);

export const getAllMotherEventRecordTypes = createAsyncThunk<
  EventRecordType[],
  undefined,
  ThunkApiType<RootState>
>(
  MotherActions.GET_MOTHER_EVENT_RECORD_TYPES,
  async (_, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      mothers: { eventRecordTypes: eventRecordTypesCache },
    } = getState();

    try {
      if (eventRecordTypesCache && eventRecordTypesCache.length > 0) {
        return eventRecordTypesCache;
      }

      if (!userAuth?.auth_token) {
        return rejectWithValue('no access token, profile check required');
      }

      const eventRecordTypes = await new MotherService(
        userAuth.auth_token
      ).getAllMotherEventRecordTypes();

      return eventRecordTypes;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateMotherAddress = createAsyncThunk<
  MotherDto,
  UpdateMotherRequest,
  ThunkApiType<RootState>
>(
  'updateMotherAddress',
  async ({ mother, id }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedMotherInput = mapMother(mother);
      if (userAuth?.auth_token) {
        return await new MotherService(
          userAuth?.auth_token
        ).updateMotherAddress(id, mappedMotherInput);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateMotherContactDetails = createAsyncThunk<
  MotherDto,
  UpdateMotherRequest,
  ThunkApiType<RootState>
>(
  'updateMotherContactDetails',
  async ({ mother, id }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let mappedMotherInput = mapMother(mother);
      if (userAuth?.auth_token) {
        return await new MotherService(
          userAuth?.auth_token
        ).updateMotherContactDetails(id, mappedMotherInput);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addAdditionalVisitForMother = createAsyncThunk<
  VisitDto,
  VisitModelInput,
  ThunkApiType<RootState>
>(
  MotherActions.ADD_ADDITIONAL_VISIT_FOR_MOTHER,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const response = await new MotherService(
          userAuth?.auth_token
        ).addAdditionalVisitForMother(input);

        return response;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getReferralsForMother = createAsyncThunk<
  VisitDataStatus[],
  { motherId: string; visitId: string },
  ThunkApiType<RootState>
>(
  MotherActions.GET_REFERRALS_FOR_MOTHER,
  async ({ motherId, visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let referrals: VisitDataStatus[];

      if (userAuth?.auth_token) {
        referrals = await new Referral(
          userAuth?.auth_token
        ).getReferralsForMother(motherId, visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!referrals) {
        return rejectWithValue('Error getting more information');
      }
      return referrals;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getCompletedReferralsForMother = createAsyncThunk<
  VisitDataStatus[],
  { motherId: string; visitId: string },
  ThunkApiType<RootState>
>(
  MotherActions.GET_COMPLETED_REFERRALS_FOR_MOTHER,
  async ({ motherId, visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let referrals: VisitDataStatus[];

      if (userAuth?.auth_token) {
        referrals = await new Referral(
          userAuth?.auth_token
        ).getCompletedReferralsForMother(motherId, visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!referrals) {
        return rejectWithValue('Error getting more information');
      }
      return referrals;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateVisitDataStatus = createAsyncThunk<
  {},
  { input: VisitDataStatusFilterInput[] },
  ThunkApiType<RootState>
>(
  MotherActions.UPDATE_VISIT_DATA_STATUS,
  async ({ input }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        new Referral(userAuth?.auth_token ?? '').updateVisitDataStatus(input);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateMotherDeliveryDate = createAsyncThunk<
  MotherDto,
  UpdateMotherDeliveryDateProps,
  ThunkApiType<RootState>
>(
  MotherActions.UPDATE_MOTHER_DELIVERY_DATE,
  async (
    { id, expectedDateOfDelivery, visits },
    { getState, rejectWithValue }
  ) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const mappedVisits = visits.map((item) => mapVisit(item));
        return await new MotherService(
          userAuth?.auth_token ?? ''
        ).updateMotherDeliveryDate(id, expectedDateOfDelivery, mappedVisits);
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const mapMother = (x: Partial<MotherDto>): MotherModelInput => ({
  userId: x.userId,
  age: x.age,
  expectedDateOfDelivery: x.expectedDateOfDelivery,
  firstName: x.firstName,
  surname: x.surname,
  healthCareWorkerId: x.healthCareWorkerId,
  phoneNumber: x.phoneNumber,
  whatsAppNumber: x.whatsAppNumber,
  siteAddress: x.siteAddress && mapSiteAddress(x.siteAddress),
  ...(x.linkedInfantId ? { linkedInfantId: x.linkedInfantId } : {}),
});

const mapSiteAddress = (x: Partial<SiteAddressDto>): SiteAddressInput => ({
  Id: x.id,
  AddressLine1: x.addressLine1,
  AddressLine2: x.addressLine2,
  AddressLine3: x.addressLine3,
  Name: x.name,
  PostalCode: x.postalCode,
  ProvinceId: x.provinceId,
  Ward: x.ward,
  IsActive: x.isActive === false ? false : true,
});

const mapVisit = (x: Partial<VisitDto>): MotherChildVisitModelInput => ({
  id: x.id,
  dueDate: x.dueDate,
  plannedVisitDate: x.plannedVisitDate,
  visitTypeId: x.visitType?.id,
  clientId: x.clientId,
  insertedDate: x.insertedDate,
});
